import styled from 'styled-components';

import NotificationStyled from '../../../../../components/Notification';
import StickyNav from '../../../../../components/StickyNav';
import { IconInfoCircle as InfoCircle } from '../../../../../core';
import UseCasePalettes from '../../../components/UseCasePalettes';

interface Props {
  className?: string;
}

const UseCaseTab = ({ className = '' }: Props) => (
  <div className={className}>
    <div className="tab-content">
      <div className="definition">
        <h2 className="title" id="definition">
          Definition
        </h2>
        <p>
          A heatmap is a data visualization technique that uses a matrix of colored cells to
          represent values, where the intensity or shade of color reflects the magnitude of data
          points. Often applied to two-dimensional grids, heatmaps are ideal for showing patterns,
          correlations, and distributions within large datasets. They are commonly used in areas
          such as performance monitoring, user behavior analysis, and geographic data
          representation.
        </p>
      </div>
      <div className="usage">
        <h2 className="title" id="usage">
          Usage
        </h2>
        <p className="subtitle">
          Heatmaps are an effective visualization tool for representing data density or intensity
          across a geographical area or a dataset:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Visualizing Data Density:</strong> Heatmaps are ideal for showing the
            concentration of data points, such as population density, traffic volume, or sales
            activity, making patterns easily identifiable at a glance.
          </li>
          <li>
            <strong>Identifying Hotspots:</strong> Heatmaps highlight areas with high activity or
            interest, such as regions with the most customer interactions or locations experiencing
            the highest demand.
          </li>
          <li>
            <strong>Tracking Changes Over Time:</strong> By overlaying data over time, heatmaps can
            reveal trends and changes, helping to analyze how variables like temperature, sales, or
            website traffic fluctuate during different periods.
          </li>
        </ul>
      </div>
      <div className="limitations">
        <h2 className="title" id="limitations">
          Limitations
        </h2>
        <p className="subtitle">
          Heatmaps are a powerful tool for visualizing data patterns, but they have certain
          limitations that should be taken into account:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Loss of Detail in Large Datasets:</strong> As heatmaps aggregate data into color
            gradients, they can obscure granular details, making it difficult to understand
            individual data points or specific variations within the data.
          </li>
          <li>
            <strong>Color Perception Issues:</strong> The interpretation of heatmaps heavily relies
            on color gradients, and color-blind users may struggle to accurately interpret the data,
            especially if the color scheme is not well-chosen.
          </li>
          <li>
            <strong>Difficult to Represent Complex Relationships:</strong> Heatmaps are effective
            for showing general patterns or intensity, but they can be limiting when trying to
            represent complex relationships between multiple variables or datasets.
          </li>
        </ul>
      </div>
      <div className="colors">
        <h2 className="title" id="colors">
          Colors
        </h2>
        <p>
          Color selection is a cornerstone of effective Heatmaps, as it directly influences the
          interpretation of data intensity and patterns. Sequential color scales are often employed,
          using light to dark shades to signify increasing data values. Diverging color palettes are
          ideal when emphasizing variations around a central value, with contrasting colors
          highlighting positive and negative differences. Careful color choices ensure that patterns
          stand out without overwhelming the viewer, making Heatmaps visually intuitive and
          analytically powerful.
        </p>
        <NotificationStyled
          icon={<InfoCircle />}
          title="For in-depth information on this topic, please refer to the Colors page."
          color="blue"
          className="info-notification"
        />
        <UseCasePalettes palettes={['sequential', 'diverging']} />
      </div>

      <div className="challenges">
        <h2 className="title">Challenges</h2>
        <p>
          Heatmaps are an effective tool for visualizing data intensity across a geographic or
          grid-based layout, but they come with specific challenges that must be addressed:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Loss of Precision:</strong> Heatmaps often represent aggregated data, which can
            obscure individual data points and reduce precision, making them unsuitable for detailed
            analyses.
          </li>
          <li>
            <strong>Color Misinterpretation:</strong> The use of color gradients can lead to
            misinterpretation, especially if the chosen palette lacks sufficient contrast or if
            viewers have color vision deficiencies.
          </li>
          <li>
            <strong>Overlapping Data Issues:</strong> In areas with high data density, the blending
            of colors can make it difficult to distinguish specific variations in intensity, leading
            to potential confusion.
          </li>
        </ul>
      </div>
    </div>
    <div>
      <StickyNav title="Contents" />
    </div>
  </div>
);

const StyledUseCaseTab = styled(UseCaseTab)`
  display: flex;
  gap: 2.5rem;
  justify-content: space-between;

  > .tab-content {
    width: 100%;
    .usage,
    .definition,
    .limitations,
    .challenges {
      max-width: 700px;
    }
    > .colors {
      > .info-notification {
        margin-top: 24px;
        margin-bottom: 40px;
      }

      > .palette {
        > .palette-title {
          color: ${({ theme }) => theme.neutralText};
          font-size: ${({ theme }) => theme.fontSizes.base};
          line-height: ${({ theme }) => theme.lineHeights.base};
          margin-bottom: 4px;
        }
      }

      .color-palette {
        margin-top: 12px;
      }
    }

    > .challenges {
      > .ordered-list-badge-num {
        margin-top: 16px;
      }
    }

    .title {
      color: ${({ theme }) => theme.neutralText};
      font-weight: 600;
      font-size: ${({ theme }) => theme.fontSizes.lg};
      line-height: ${({ theme }) => theme.lineHeights.lg};
      margin-bottom: 8px;
      margin-top: 40px;
    }

    p {
      color: ${({ theme }) => theme.neutralTextWeak};
      font-size: ${({ theme }) => theme.fontSizes.sm};
      line-height: ${({ theme }) => theme.lineHeights.sm};
      font-weight: 400;
      max-width: 700px;
    }

    .subtitle {
      margin-bottom: 16px;
    }

    // styles for list with badge
    .ordered-list-badge-num {
      font-size: 0.875rem;
      line-height: 1.4285714286;
      margin-left: 29px;
      counter-reset: num;
      list-style: none;
      color: ${({ theme }) => theme.neutralTextWeak};

      li {
        position: relative;
        margin-bottom: 16px;

        &::before {
          content: counter(num);
          counter-increment: num;
          position: absolute;
          left: -1.75rem;
          top: 0;
          width: 1.25rem;
          height: 1.25rem;
          border-radius: 0.1875rem;
          text-align: center;
          font-size: 0.6875rem;
          line-height: 1.8;
          color: ${({ theme }) => theme.primaryText};
          background-color: ${({ theme }) => theme.primaryBackgroundActive};
        }
      }
    }
  }
`;

export default StyledUseCaseTab;
